import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps {
  children: React.ReactElement;
  content: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
}

export function Tooltip({ children, content, position = 'right' }: TooltipProps) {
  const [show, setShow] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const showTimeoutRef = useRef<NodeJS.Timeout>();
  const hideTimeoutRef = useRef<NodeJS.Timeout>();
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  const getTooltipPosition = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return {};

    switch (position) {
      case 'top':
        return {
          left: rect.left + rect.width / 2,
          top: rect.top - 8,
          transform: 'translate(-50%, -100%)'
        };
      case 'bottom':
        return {
          left: rect.left + rect.width / 2,
          top: rect.bottom + 8,
          transform: 'translate(-50%, 0)'
        };
      case 'left':
        return {
          left: rect.left - 8,
          top: rect.top + rect.height / 2,
          transform: 'translate(-100%, -50%)'
        };
      case 'right':
      default:
        return {
          left: rect.right + 8,
          top: rect.top + rect.height / 2,
          transform: 'translate(0, -50%)'
        };
    }
  };

  const handleShow = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }
    showTimeoutRef.current = setTimeout(() => setShow(true), 200);
  };

  const handleHide = () => {
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
    }
    hideTimeoutRef.current = setTimeout(() => setShow(false), 200);
  };

  useEffect(() => {
    return () => {
      if (showTimeoutRef.current) clearTimeout(showTimeoutRef.current);
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, []);

  const tooltipContent = show && createPortal(
    <div
      ref={tooltipRef}
      role="tooltip"
      aria-hidden={!show}
      style={{
        position: 'fixed',
        ...getTooltipPosition(),
        zIndex: 9999,
      }}
      className="px-4 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg"
    >
      {content}
    </div>,
    document.body
  );

  return (
    <div
      ref={triggerRef}
      onMouseEnter={() => !isMobile && handleShow()}
      onMouseLeave={() => !isMobile && handleHide()}
      onClick={() => isMobile && setShow(!show)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setShow(!show);
        }
        if (e.key === 'Escape') {
          setShow(false);
        }
      }}
      role="button"
      tabIndex={0}
      aria-describedby={show ? 'tooltip' : undefined}
    >
      {children}
      {tooltipContent}
    </div>
  );
} 